import { getEmbedContext } from '@/utils/getEmbedContext';

import { TrackingEmbedMode, TrackingEmbedSource } from './TrackingRequest';

// Which page of the partner's site the widget is embedded on, and how sure we are.
//
// Inside a cross-origin iframe the browser shows us the parent page's ORIGIN and
// nothing more — the path is deliberately withheld unless the partner opts in.
// So the page is resolved from the best available source, and `embedSource`
// records which one so a report can tell an exact page from an origin with a
// bare "/" (which is what 99% of iframe sessions look like from the referrer).
//
//   urlParam        the partner's `url=` iframe parameter — exact, every browser
//   location        the widget is the page (code snippet, or top-level on our host)
//   referrer        document.referrer — the path only if the partner's referrer
//                   policy allowed it; usually origin + "/"
//   ancestorOrigin  location.ancestorOrigins — origin only
export type EmbedPage = {
    embedMode?: TrackingEmbedMode;
    embedOrigin?: string;
    embedPath?: string;
    embedSource?: TrackingEmbedSource;
};

type SplitUrl = { origin: string; path: string };

// The API's caps, mirrored so a partner's very long slug is truncated on the way
// out rather than failing the contract and losing the batch.
const MAX_ORIGIN_LENGTH = 255;
const MAX_PATH_LENGTH = 500;

function page(origin: string, path: string): SplitUrl {
    return { origin: origin.slice(0, MAX_ORIGIN_LENGTH), path: path.slice(0, MAX_PATH_LENGTH) };
}

// The query string and fragment are deliberately discarded. A partner's own URLs
// may carry anything in them — customer references, email addresses, session
// tokens — and this collection has no business storing a third party's query
// parameters. The path is what identifies the page.
function splitUrl(url: string): SplitUrl | null {
    try {
        const parsed = new URL(url);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
        return page(parsed.origin, parsed.pathname);
    } catch (error) {
        return null;
    }
}

export function getEmbedPage(): EmbedPage {
    try {
        const { embedMode, pageUrl, parentUrl, ancestorOrigin } = getEmbedContext();
        if (!embedMode) return {};

        // The partner told us. Trusted over what the browser volunteers because
        // it is the only source that carries the path in Safari and Firefox.
        //
        // Deliberately not cross-checked against the ancestor origin: partners
        // legitimately pass a canonical URL whose host differs from the page's
        // (www vs bare, a stock subdomain), and a hand-off carries a page from
        // another origin on purpose. Every field on this endpoint is
        // client-supplied anyway; a forged value can only mislabel a page inside
        // the forger's own vroom code.
        if (pageUrl) {
            const page = splitUrl(pageUrl);
            if (page) {
                return {
                    embedMode,
                    embedOrigin: page.origin,
                    embedPath: page.path,
                    embedSource: 'urlParam',
                };
            }
        }

        // The widget is the page: rendered into the partner's DOM by the snippet,
        // or opened top-level on our own host. document.referrer would be the
        // PREVIOUS page here, which is why it is never consulted in these modes.
        if (embedMode === 'snippet' || embedMode === 'direct') {
            if (typeof window === 'undefined') return { embedMode };
            const here = page(window.location.origin, window.location.pathname);
            return { embedMode, embedOrigin: here.origin, embedPath: here.path, embedSource: 'location' };
        }

        // iframe. The referrer is the immediate parent, so its path only describes
        // the partner's page when the immediate parent IS the top-level page. Under
        // a site builder's sandbox frame (Wix, Staticly) it describes the sandbox.
        if (parentUrl) {
            const parent = splitUrl(parentUrl);
            const referrerIsTopPage = !!parent && (!ancestorOrigin || parent.origin === ancestorOrigin);

            if (parent && referrerIsTopPage) {
                return {
                    embedMode,
                    // ancestorOrigins cannot be blanked or spoofed by a referrer
                    // policy, so it is the more trustworthy origin where present.
                    embedOrigin: ancestorOrigin ?? parent.origin,
                    embedPath: parent.path,
                    embedSource: 'referrer',
                };
            }
        }

        // Referrer blanked, unparseable, or belonging to a sandbox frame: the
        // origin alone is still worth having. Recorded without a path rather
        // than as a misleading "/".
        if (ancestorOrigin) {
            return { embedMode, embedOrigin: ancestorOrigin, embedSource: 'ancestorOrigin' };
        }

        return { embedMode };
    } catch (error) {
        return {};
    }
}
